/**
 * Description: Shared product detail fields used in both inline and full product forms
 */

import React from 'react'
import { UseFormRegister, FieldErrors } from 'react-hook-form'
import { Product } from '@services/api'

interface ProductDetailsFieldsProps {
  register: UseFormRegister<Product>
  errors: FieldErrors<Product>
  compact?: boolean
}

const ProductDetailsFields: React.FC<ProductDetailsFieldsProps> = ({ register, errors, compact = false }) => {
  const gridClass = compact ? 'grid grid-cols-2 gap-3' : 'grid grid-cols-1 md:grid-cols-2 gap-6'
  const labelClass = compact
    ? 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
    : 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
  const statusLabelClass = compact
    ? 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'
    : 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3'
  const statusGapClass = compact ? 'flex gap-6' : 'flex gap-6'
  const colSpan = compact ? '' : 'md:col-span-2'

  return (
    <>
      {/* Product Name - Required */}
      <div className={colSpan}>
        <label className={labelClass}>
          Product Name <span className="text-red-500">*</span>
        </label>
        <input
          {...register('product_name', { required: 'Product name is required' })}
          className="input-field"
          placeholder="Enter product name"
        />
        {errors.product_name && <p className="mt-1 text-sm text-red-600">{errors.product_name.message}</p>}
      </div>

      {/* Description */}
      <div className={colSpan}>
        <label className={labelClass}>Description</label>
        <textarea
          {...register('description')}
          rows={compact ? 2 : 3}
          className="input-field"
          placeholder={compact ? 'Enter description' : 'Enter product description'}
        />
      </div>

      {/* Product Code & Category in a grid */}
      <div className={`${gridClass} ${colSpan}`}>
        <div>
          <label className={labelClass}>Product Code</label>
          <input {...register('product_code')} className="input-field" placeholder="e.g., PRD-001" />
        </div>
        <div>
          <label className={labelClass}>Category</label>
          <input
            {...register('category')}
            className="input-field"
            placeholder={compact ? 'e.g., Software' : 'e.g., Software, Hardware'}
          />
        </div>
      </div>

      {/* Status */}
      <div className={colSpan}>
        <label className={statusLabelClass}>Status</label>
        <div className={statusGapClass}>
          {[
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'deprecated', label: 'Deprecated' },
          ].map((option) => (
            <label key={option.value} className="flex cursor-pointer items-center space-x-2">
              <input
                type="radio"
                value={option.value}
                {...register('status')}
                className="text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">{option.label}</span>
            </label>
          ))}
        </div>
      </div>
    </>
  )
}

export default ProductDetailsFields
